import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { supabase, buildCurrentUser } from '../lib/supabase';
import './AuthPages.css';

export default function LoginPage({ onLogin }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Forgot password state
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotError, setForgotError] = useState('');

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password });
      if (authError) throw authError;

      const currentUser = await buildCurrentUser(data.user);
      await onLogin(currentUser, data.session.access_token);
      // Existing users always go to home (onboarding only for new signups)
      navigate('/home');
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });
      if (error) throw error;
    } catch (err) {
      setError(err.message || 'Failed to sign in with Google');
      setGoogleLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotMessage('');
    setForgotLoading(true);

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (resetError) throw resetError;
      setForgotMessage(
        'If an account exists for that email, a password reset link has been sent. Please check your inbox.'
      );
    } catch (err) {
      setForgotError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  if (forgotMode) {
    return (
      <div className="auth-container">
        <div className="auth-box">
          <div className="auth-logo">
            <img
              src="https://wavcfonfn7v2m.ok.kimi.link/images/black.png"
              alt="DealNexa"
              className="auth-logo-img"
            />
          </div>

          <h2 className="auth-title">Reset Password</h2>
          <p className="auth-subtitle">Enter your registered email and we'll send you a reset link.</p>

          {forgotMessage && <div className="auth-success">{forgotMessage}</div>}
          {forgotError && <div className="auth-error">{forgotError}</div>}

          {!forgotMessage && (
            <form onSubmit={handleForgotPassword} className="auth-form">
              <div className="auth-field">
                <label htmlFor="forgot-email">Email</label>
                <input
                  type="email"
                  id="forgot-email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  placeholder="Enter your registered email"
                />
              </div>

              <button type="submit" className="auth-submit" disabled={forgotLoading}>
                {forgotLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          )}

          <button
            className="auth-back"
            onClick={() => {
              setForgotMode(false);
              setForgotEmail('');
              setForgotMessage('');
              setForgotError('');
            }}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="auth-container">
      <div className="auth-box">
        <div className="auth-logo">
          <img
            src="https://wavcfonfn7v2m.ok.kimi.link/images/black.png"
            alt="DealNexa"
            className="auth-logo-img"
          />
        </div>

        <h2 className="auth-title">Welcome Back</h2>

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleEmailLogin} className="auth-form">
          <div className="auth-field">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
            />
          </div>

          <div className="auth-field">
            <div className="auth-field-label-row">
              <label htmlFor="password">Password</label>
              <button
                type="button"
                className="auth-forgot-link"
                onClick={() => {
                  setForgotEmail(email);
                  setForgotMode(true);
                }}
              >
                Forgot password?
              </button>
            </div>
            <div className="auth-password-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
              />
              <button
                type="button"
                className="auth-eye-btn"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button type="submit" className="auth-submit" disabled={loading}>
            {loading ? 'Logging in...' : 'Log In'}
          </button>
        </form>

        <div className="auth-divider">
          <span>or</span>
        </div>

        <button 
          className="google-signin-btn" 
          onClick={handleGoogleSignIn}
          disabled={googleLoading}
        >
          {googleLoading ? (
            'Connecting...'
          ) : (
            <>
              <svg width="18" height="18" viewBox="0 0 18 18">
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.842 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                <path fill="#34A853" d="M9.003 18c2.43 0 4.467-.806 5.956-2.18l-2.909-2.26c-.806.54-1.836.86-3.047.86-2.344 0-4.328-1.58-5.036-3.711H.96v2.332C2.44 15.983 5.48 18 9.003 18z"/>
                <path fill="#FBBC05" d="M3.964 10.712c-.18-.54-.282-1.116-.282-1.71s.102-1.17.282-1.71V4.96H.96C.348 6.173 0 7.548 0 9.002s.348 2.829.96 4.042l3.004-2.332z"/>
                <path fill="#EA4335" d="M9.003 3.58c1.32 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.428 0 9.003 0 5.48 0 2.44 2.017.96 4.958L3.964 7.29c.708-2.13 2.692-3.71 5.039-3.71z"/>
              </svg>
              Continue with Google
            </>
          )}
        </button>

        <p className="auth-switch">
          Don't have an account?{' '}
          <Link to="/signup">Sign up</Link>
        </p>

        <button className="auth-back" onClick={() => navigate('/')}>
          Back to home
        </button>
      </div>

      <style>{`
        .google-signin-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          padding: 0.875rem;
          border: 1px solid #e5e5e5;
          border-radius: 8px;
          background: #ffffff;
          color: #374151;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .google-signin-btn:hover:not(:disabled) {
          background: #f9fafb;
          border-color: #d1d5db;
        }
        
        .google-signin-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .google-icon {
          width: 20px;
          height: 20px;
        }
        
        .auth-terms {
          text-align: center;
          margin-top: 1rem;
          color: #9ca3af;
          font-size: 0.75rem;
        }
        
        .auth-terms a {
          color: #6b7280;
          text-decoration: underline;
        }
        
        .auth-terms a:hover {
          color: #111111;
        }
      `}</style>
    </div>
  );
}
